<template>
  <!--
    This example requires updating your template:

    ```
    <html class="h-full bg-gray-50">
    <body class="h-full">
    ```
  -->
  <div class="h-full">
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog
        as="div"
        class="relative z-50 lg:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div
                  class="absolute left-full top-0 flex w-16 justify-center pt-5"
                >
                  <button
                    type="button"
                    class="-m-2.5 p-2.5"
                    @click="sidebarOpen = false"
                  >
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex h-full flex-col bg-blue-600">
        <!-- Logo - Fixed at top -->
        <div class="flex h-16 shrink-0 items-center px-6">
          <LogoIconFactory />
        </div>
        
        <!-- Scrollable Navigation Area -->
        <nav class="flex flex-1 flex-col overflow-y-auto px-6">
          <ul role="list" class="flex flex-1 flex-col gap-y-7 py-5">
            <li>
              <ul role="list" class="-mx-2 space-y-1">
                <li
                  v-for="item in navigation"
                  :key="item.name"
                  class="cursor-pointer"
                >
                  <a
                    @click="changeRoute(item.routeName)"
                    :class="[
                      item.current
                        ? 'bg-gray-50 text-SonicSpecBrandColor'
                        : 'text-[#fcffff] hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        item.current
                          ? 'text-SonicSpecBrandColor'
                          : 'text-blue-300 group-hover:text-SonicSpecBrandColor',
                        'h-6 w-6 shrink-0',
                      ]"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </a>
                </li>
              </ul>
              
              <!-- Internal Tools Section -->
              <div v-if="hasInternalTools" class="mt-3">
                <button
                  @click="toggleInternalTools"
                  class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-medium text-blue-200 hover:bg-white/5 transition-colors"
                >
                  <span class="text-blue-200/80">Internal Tools</span>
                  <ChevronDownIcon
                    :class="[
                      'h-4 w-4 text-blue-200/80 transition-transform duration-200',
                      internalToolsExpanded ? 'rotate-180' : ''
                    ]"
                  />
                </button>
                <div
                  class="overflow-hidden transition-all duration-200"
                  :style="{
                    maxHeight: internalToolsExpanded ? `${internalNavigation.length * 44}px` : '0px'
                  }"
                >
                  <ul role="list" class="mt-1 space-y-1">
                    <li
                      v-for="item in internalNavigation"
                      :key="item.name"
                      class="cursor-pointer"
                    >
                      <a
                        @click="changeRoute(item.routeName)"
                        :class="[
                          item.current
                            ? 'bg-gray-50 text-SonicSpecBrandColor'
                            : 'text-[#fcffff] hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                          'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                        ]"
                      >
                        <component
                          :is="item.icon"
                          :class="[
                            item.current
                              ? 'text-SonicSpecBrandColor'
                              : 'text-blue-300 group-hover:text-SonicSpecBrandColor',
                            'h-6 w-6 shrink-0',
                          ]"
                          aria-hidden="true"
                        />
                        {{ item.name }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
            <li>
              <div
                v-if="teams?.length"
                class="text-xs font-semibold leading-6 text-gray-400"
              >
                Your teams
              </div>
              <ul role="list" class="-mx-2 mt-2 space-y-1">
                <li v-for="team in teams" :key="team.name">
                  <a
                    :href="team.href"
                    :class="[
                      team.current
                        ? 'bg-gray-50 text-SonicSpecBrandColor'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    ]"
                  >
                    <span
                      :class="[
                        team.current
                          ? 'border-SonicSpecBrandColor text-SonicSpecBrandColor'
                          : 'border-gray-200 text-gray-400 group-hover:border-SonicSpecBrandColor group-hover:text-SonicSpecBrandColor',
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-pureWhite text-[0.625rem] font-medium',
                      ]"
                      >{{ team.initial }}</span
                    >
                    <span class="truncate">{{ team.name }}</span>
                  </a>
                </li>
              </ul>
            </li>
            <li class="mt-auto">
              <!-- Carousel Container -->
              <div class="relative mb-3 overflow-hidden">
                <!-- Carousel Track -->
                <div 
                  class="flex transition-transform duration-500 ease-out"
                  :style="{ transform: `translateX(-${carouselIndex * 100}%)` }"
                >
                  <!-- Slide 1: Email Forwarding (Compact) -->
                  <div class="w-full flex-shrink-0">
                    <div class="rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 p-2.5">
                      <h3 class="text-xs font-semibold text-white mb-1.5">Forward requests by email</h3>
                      <div class="flex items-center gap-1.5 mb-1.5">
                        <code class="flex-1 text-[11px] font-mono text-blue-100 bg-white/10 px-1.5 py-1 rounded truncate leading-tight">
                          agent@quote.sonicspec.com
                        </code>
                        <button
                          @click="copyEmailToClipboard"
                          class="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
                          title="Copy email"
                        >
                          <svg v-if="!emailCopied" class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <svg v-else class="h-3.5 w-3.5 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      </div>
                      <p class="text-[11px] text-blue-100 leading-snug">
                        All requests sent here will be added to your inbox.
                      </p>
                    </div>
                  </div>
                  
                  <!-- Slide 2: Download App -->
                  <div class="w-full flex-shrink-0 px-0">
                    <DownloadAppButton />
                  </div>
                </div>
                
                <!-- Carousel Dots -->
                <div class="flex justify-center gap-1.5 mt-2">
                  <button
                    v-for="(_, index) in 2"
                    :key="index"
                    @click="handleCarouselDotClick(index)"
                    class="transition-all duration-300"
                    :class="[
                      carouselIndex === index 
                        ? 'w-6 h-1.5 bg-white rounded-full' 
                        : 'w-1.5 h-1.5 bg-white/40 rounded-full hover:bg-white/60'
                    ]"
                    :aria-label="`Go to slide ${index + 1}`"
                  />
                </div>
              </div>
            </li>
          </ul>
        </nav>
        
        <!-- Fixed Profile Section at Bottom -->
        <div class="shrink-0 border-t border-white/10 bg-blue-600">
          <ul role="list">
            <li class="group">
              <a
                target="_blank"
                href="https://intercom.help/sonicspec/en/"
                class="flex items-center gap-x-3 px-6 py-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50"
              >
                <QuestionMarkCircleIcon
                  class="h-6 w-6 text-[#FFFFFF] group-hover:text-SonicSpecBrandColor"
                ></QuestionMarkCircleIcon>
                <span class="sr-only">Help Center</span>
                <span
                  aria-hidden="true"
                  class="font-semibold text-[#fcffff] group-hover:text-SonicSpecBrandColor"
                  >Help Center</span
                >
                <ArrowTopRightOnSquareIcon
                  class="hidden h-4 text-gray-400 group-hover:block group-hover:text-SonicSpecBrandColor"
                ></ArrowTopRightOnSquareIcon>
              </a>
            </li>
            <li class="group">
              <router-link
                to="/profile"
                class="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-50 hover:text-SonicSpecBrandColor"
              >
                <span
                  class="border-gray-200 text-[#475467] group-hover:border-blue-100 group-hover:text-SonicSpecBrandColor
                    flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-pureWhite text-[1rem] font-semibold"
                  >{{ username.charAt(0).toUpperCase() }}</span
                >
                <span class="sr-only">Your profile</span>
                <section class="flex flex-col gap-[0px] flex-1 min-w-0">
                  <span aria-hidden="true" class="text-[#FFFFFF] font-semibold group-hover:text-SonicSpecBrandColor truncate">{{ username }}</span>
                  <span v-if="email" aria-hidden="true" class="text-[#fcffff] group-hover:text-SonicSpecBrandColor truncate text-xs">{{ email }}</span>
                </section>
                <div @click.prevent.stop="logout" class="hover:border-[#84CAFF] hover:border rounded-md p-1 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#84CAFF" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
                  </svg>
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div
      class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col"
    >
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex h-full flex-col border-r border-gray-200 bg-blue-600">
        <!-- Logo - Fixed at top -->
        <div class="flex h-16 shrink-0 items-center px-6">
          <LogoIconFactory />
        </div>
        
        <!-- Scrollable Navigation Area -->
        <nav class="flex flex-1 flex-col overflow-y-auto px-6">
          <ul role="list" class="flex flex-1 flex-col gap-y-7 py-5">
            <li>
              <ul role="list" class="-mx-2 space-y-1">
                <li
                  v-for="item in navigation"
                  :key="item.name"
                  class="cursor-pointer"
                >
                  <a
                    @click="changeRoute(item.routeName)"
                    :class="[
                      item.current
                        ? 'bg-gray-50 text-SonicSpecBrandColor'
                        : 'text-[#fcffff] hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        item.current
                          ? 'text-SonicSpecBrandColor'
                          : 'text-blue-300 group-hover:text-SonicSpecBrandColor',
                        'h-6 w-6 shrink-0',
                      ]"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </a>
                </li>
              </ul>
              
              <!-- Internal Tools Section -->
              <div v-if="hasInternalTools" class="mt-3">
                <button
                  @click="toggleInternalTools"
                  class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-medium text-blue-200 hover:bg-white/5 transition-colors"
                >
                  <span class="text-blue-200/80">Internal Tools to SonicSpec</span>
                  <ChevronDownIcon
                    :class="[
                      'h-4 w-4 text-blue-200/80 transition-transform duration-200',
                      internalToolsExpanded ? 'rotate-180' : ''
                    ]"
                  />
                </button>
                <div
                  class="overflow-hidden transition-all duration-200"
                  :style="{
                    maxHeight: internalToolsExpanded ? `${internalNavigation.length * 44}px` : '0px'
                  }"
                >
                  <ul role="list" class="mt-1 space-y-1">
                    <li
                      v-for="item in internalNavigation"
                      :key="item.name"
                      class="cursor-pointer"
                    >
                      <a
                        @click="changeRoute(item.routeName)"
                        :class="[
                          item.current
                            ? 'bg-gray-50 text-SonicSpecBrandColor'
                            : 'text-[#fcffff] hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                          'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                        ]"
                      >
                        <component
                          :is="item.icon"
                          :class="[
                            item.current
                              ? 'text-SonicSpecBrandColor'
                              : 'text-blue-300 group-hover:text-SonicSpecBrandColor',
                            'h-6 w-6 shrink-0',
                          ]"
                          aria-hidden="true"
                        />
                        {{ item.name }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
            <li>
              <div
                class="text-xs font-semibold leading-6 text-gray-400"
                v-if="teams?.length"
              >
                Your teams
              </div>
              <ul role="list" class="-mx-2 mt-2 space-y-1">
                <li v-for="team in teams" :key="team.name">
                  <a
                    :href="team.href"
                    :class="[
                      team.current
                        ? 'bg-gray-50 text-SonicSpecBrandColor'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-SonicSpecBrandColor',
                      'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                    ]"
                  >
                    <span
                      :class="[
                        team.current
                          ? 'border-SonicSpecBrandColor text-SonicSpecBrandColor'
                          : 'border-gray-200 text-gray-400 group-hover:border-SonicSpecBrandColor group-hover:text-SonicSpecBrandColor',
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-pureWhite text-[0.625rem] font-medium',
                      ]"
                      >{{ team.initial }}</span
                    >
                    <span class="truncate">{{ team.name }}</span>
                  </a>
                </li>
              </ul>
            </li>
            <li class="mt-auto">
              <!-- Carousel Container -->
              <div class="relative mb-3 overflow-hidden">
                <!-- Carousel Track -->
                <div 
                  class="flex transition-transform duration-500 ease-out"
                  :style="{ transform: `translateX(-${carouselIndex * 100}%)` }"
                >
                  <!-- Slide 1: Email Forwarding (Compact) -->
                  <div class="w-full flex-shrink-0">
                    <div class="rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 p-2.5">
                      <h3 class="text-xs font-semibold text-white mb-1.5">Forward requests by email</h3>
                      <div class="flex items-center gap-1.5 mb-1.5">
                        <code class="flex-1 text-[11px] font-mono text-blue-100 bg-white/10 px-1.5 py-1 rounded truncate leading-tight">
                          agent@quote.sonicspec.com
                        </code>
                        <button
                          @click="copyEmailToClipboard"
                          class="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
                          title="Copy email"
                        >
                          <svg v-if="!emailCopied" class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <svg v-else class="h-3.5 w-3.5 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      </div>
                      <p class="text-[11px] text-blue-100 leading-snug">
                        All requests sent here will be added to your inbox.
                      </p>
                    </div>
                  </div>
                  
                  <!-- Slide 2: Download App -->
                  <div class="w-full flex-shrink-0 px-0">
                    <DownloadAppButton />
                  </div>
                </div>
                
                <!-- Carousel Dots -->
                <div class="flex justify-center gap-1.5 mt-2">
                  <button
                    v-for="(_, index) in 2"
                    :key="index"
                    @click="handleCarouselDotClick(index)"
                    class="transition-all duration-300"
                    :class="[
                      carouselIndex === index 
                        ? 'w-6 h-1.5 bg-white rounded-full' 
                        : 'w-1.5 h-1.5 bg-white/40 rounded-full hover:bg-white/60'
                    ]"
                    :aria-label="`Go to slide ${index + 1}`"
                  />
                </div>
              </div>
            </li>
          </ul>
        </nav>
        
        <!-- Fixed Profile Section at Bottom -->
        <div class="shrink-0 border-t border-white/10 bg-blue-600">
          <ul role="list">
            <li class="group">
              <a
                target="_blank"
                href="https://intercom.help/sonicspec/en/"
                class="flex items-center gap-x-3 px-6 py-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50"
              >
                <QuestionMarkCircleIcon
                  class="h-6 w-6 text-[#FFFFFF] group-hover:text-SonicSpecBrandColor"
                ></QuestionMarkCircleIcon>
                <span class="sr-only">Help Center</span>
                <span
                  aria-hidden="true"
                  class="font-semibold text-[#fcffff] group-hover:text-SonicSpecBrandColor"
                  >Help Center</span
                >
                <ArrowTopRightOnSquareIcon
                  class="hidden h-4 text-gray-400 group-hover:block group-hover:text-SonicSpecBrandColor"
                ></ArrowTopRightOnSquareIcon>
              </a>
            </li>
            <li class="group">
              <router-link
                to="/profile"
                class="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-blue-200 hover:bg-gray-50 hover:text-SonicSpecBrandColor"
              >
                <span
                  class="border-gray-200 text-[#475467] group-hover:border-blue-100 group-hover:text-SonicSpecBrandColor
                    flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-pureWhite text-[1rem] font-semibold"
                  >{{ username.charAt(0).toUpperCase() }}</span
                >
                <span class="sr-only">Your profile</span>
                <section class="flex flex-col gap-[0px] flex-1 min-w-0">
                  <span aria-hidden="true" class="text-[#FFFFFF] font-semibold group-hover:text-SonicSpecBrandColor truncate">{{ username }}</span>
                  <span v-if="email" aria-hidden="true" class="text-[#fcffff] group-hover:text-SonicSpecBrandColor truncate text-xs">{{ email }}</span>
                </section>
                <div @click.prevent.stop="logout" class="hover:border-[#84CAFF] hover:border rounded-md p-1 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#84CAFF" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
                  </svg>
                </div>
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      class="sticky top-0 z-40 flex items-center gap-x-6 bg-pureWhite px-4 py-4 shadow-sm sm:px-6 lg:hidden"
    >
      <button
        type="button"
        class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        @click="sidebarOpen = true"
      >
        <span class="sr-only">Open sidebar</span>
        <Bars3Icon class="h-6 w-6" aria-hidden="true" />
      </button>
      <div class="flex-1 text-sm font-semibold leading-6 text-gray-900">
        Dashboard
      </div>
      <a href="#">
        <span class="sr-only">Your profile</span>
        <span
          :class="[
            false
              ? 'border-SonicSpecBrandColor text-SonicSpecBrandColor'
              : 'border-gray-200 text-[#475467] group-hover:border-SonicSpecBrandColor group-hover:text-SonicSpecBrandColor',
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border bg-pureWhite text-[0.625rem] font-medium',
          ]"
          >{{ username.charAt(0).toUpperCase() }}</span
        >
      </a>
    </div>

    <main class="flex min-h-full flex-col bg-pureWhite lg:pl-72">
      <div class="grow h-full px-4 sm:px-6 lg:px-8">
        <!-- Your content -->
        <slot></slot>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  Bars3Icon,
  FolderIcon,
  QuestionMarkCircleIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  BookOpenIcon,
  TagIcon,
  CodeBracketSquareIcon,
  CodeBracketIcon,
  InboxIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from '@heroicons/vue/24/outline';
import IconSpecifiers from '@/components/icons/IconSpecifiers.vue';
import LogoIconFactory from '@/components/icons/LogoIconFactory.vue';
import StackShare from '@/components/icons/stackShared.vue';
import { resetAnalytics, trackEvent } from '@/services/analytics.service';
import { stytch } from '@/stytch';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import DownloadAppButton from '@/components/tailwindCSS/DownloadAppButton.vue';

const router = useRouter();
const authStore = useAuthStore();
const props = defineProps({
  currRoute: {
    type: String,
    default: '',
  },
  userName: {
    type: String,
    default: sessionStorage.getItem('username') || 'SonicSpec',
  },
});

// Reactive computed properties that update when authStore changes
const email = computed(() => sessionStorage.getItem('email') || authStore.email || null);
const username = computed(() => sessionStorage.getItem('username') || authStore.username || props.userName || 'SonicSpec');
const internalToolsExpanded = ref(false);

function createNavItem(name, routeName, icon) {
  return {
    name,
    routeName,
    icon,
    get current() {
      return props.currRoute === this.routeName;
    },
  };
}

const navigation = computed(() => {
  return [
    createNavItem('Inbox', 'Inbox Viewer', InboxIcon),
    createNavItem('Projects', 'Project Viewer', FolderIcon),
    createNavItem('Shared Projects', 'Shared Projects Viewer', StackShare),
    createNavItem('Library', 'Library Viewer', BookOpenIcon),
    createNavItem('Knowledge Exchange', 'Knowledge Exchange Viewer', GlobeAltIcon),
    createNavItem('Tags', 'Tags Viewer', TagIcon),
    createNavItem('Product Lines', 'Product Lines', CodeBracketSquareIcon),
    createNavItem('Product Types', 'Product Types', CodeBracketIcon),
    createNavItem('Specifiers', 'Specifier Viewer', IconSpecifiers),
  ];
});

const internalNavigation = computed(() => {
  if (email.value && email.value.endsWith('@sonicspec.com')) {
    return [
      createNavItem('Discover Organizations', 'Discover Organization Viewer', IconSpecifiers),
    ];
  }
  return [];
});

const hasInternalTools = computed(() => internalNavigation.value.length > 0);

function toggleInternalTools() {
  internalToolsExpanded.value = !internalToolsExpanded.value;
  trackEvent('Internal Tools Toggle', {
    expanded: internalToolsExpanded.value,
  });
}

// const teams = [
//   { id: 1, name: 'Heroicons', href: '#', initial: 'H', current: false },
//   { id: 2, name: 'Tailwind Labs', href: '#', initial: 'T', current: false },
//   { id: 3, name: 'Workcation', href: '#', initial: 'W', current: false },
// ];

const teams = [];

function changeRoute(routeName) {
  router.push({ name: routeName });
  trackEvent('Navigation Change', {
    routeName,
  });
}

const sidebarOpen = ref(false);
const emailCopied = ref(false);
const carouselIndex = ref(0);
let carouselInterval = null;

function logout() {
    authStore.logout(router);
}

async function copyEmailToClipboard() {
  const email = 'agent@quote.sonicspec.com';
  try {
    await navigator.clipboard.writeText(email);
    emailCopied.value = true;
    trackEvent('Copy Forwarding Email', {
      email,
    });
    setTimeout(() => {
      emailCopied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy email:', err);
  }
}

// Auto-rotate carousel every 5 seconds
function startCarouselRotation() {
  carouselInterval = setInterval(() => {
    carouselIndex.value = (carouselIndex.value + 1) % 2;
  }, 4000);
}

function stopCarouselRotation() {
  if (carouselInterval) {
    clearInterval(carouselInterval);
    carouselInterval = null;
  }
}

function handleCarouselDotClick(index) {
  carouselIndex.value = index;
  // Reset auto-rotation timer on manual interaction
  stopCarouselRotation();
  startCarouselRotation();
  trackEvent('Carousel Navigation', {
    slide: index === 0 ? 'Forward Email' : 'Download App',
  });
}

onMounted(() => {
  startCarouselRotation();
});

onUnmounted(() => {
  stopCarouselRotation();
});
</script>
